import React, { useState, useRef, useEffect } from "react";
import styles from "./TerminalManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Terminal } from "../../types";
import TerminalPositioner2D from "../../components/TerminalPositioner2D/TerminalPositioner2D";

interface TerminalFormData {
  name: string;
  floor: number | "";
  mapIconInactiveUrl: string;
  mapIconActiveUrl: string;
  scaleX: number;
  scaleY: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  isActive: boolean;
}

export default function TerminalManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<Terminal | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const initialFormData: TerminalFormData = {
    name: "",
    floor: "",
    mapIconInactiveUrl: "",
    mapIconActiveUrl: "",
    scaleX: 1.0,
    scaleY: 1.0,
    positionX: 0,
    positionY: 0,
    positionZ: 0,
    isActive: true,
  };

  const [formData, setFormData] = useState<TerminalFormData>(initialFormData);

  const { data: terminals, isLoading: terminalsLoading } = useQuery({
    queryKey: ["terminals"],
    queryFn: () => apiService.getTerminals(),
  });

  const { data: floors } = useQuery({
    queryKey: ["floors"],
    queryFn: () => apiService.getFloors(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createTerminal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["terminals"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Стенд успешно создан!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setError(error.response?.data?.message || "Ошибка при создании стенда");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiService.updateTerminal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["terminals"] });
      setIsModalOpen(false);
      resetForm();
      setSuccess("Стенд успешно обновлен!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        setError(error.response?.data?.message || "Ошибка при обновлении стенда");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteTerminal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["terminals"] });
      setSuccess("Стенд успешно удален!");
      setTimeout(() => setSuccess(""), 3000);
    },
    onError: (error: any) => {
      console.error("[deleteMutation] Error:", error);
      console.error("[deleteMutation] Error response data:", error?.response?.data);
      
      const errorMessage = error.response?.data?.message || "Ошибка при удалении стенда";
      const errorDetails = error.response?.data?.details;
      
      // Если есть детали ошибки, добавляем их к сообщению
      const fullMessage = errorDetails 
        ? `${errorMessage}. ${errorDetails}` 
        : errorMessage;
      
      setError(fullMessage);
    },
  });

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingTerminal(null);
    setError("");
  };

  const handleAddNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleEdit = (terminal: Terminal) => {
    setEditingTerminal(terminal);
    setFormData({
      name: terminal.name || "",
      floor: terminal.floor,
      mapIconInactiveUrl: terminal.mapIconInactiveUrl || "",
      mapIconActiveUrl: terminal.mapIconActiveUrl || "",
      scaleX:
        terminal.scaleX !== undefined && terminal.scaleX !== null
          ? Number(terminal.scaleX)
          : 1.0,
      scaleY:
        terminal.scaleY !== undefined && terminal.scaleY !== null
          ? Number(terminal.scaleY)
          : 1.0,
      positionX: Number(terminal.positionX) || 0,
      positionY: Number(terminal.positionY) || 0,
      positionZ: Number(terminal.positionZ) || 0,
      isActive: terminal.isActive,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Вы уверены, что хотите удалить этот стенд?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationMessages: string[] = [];

    if (formData.floor === "") {
      validationMessages.push("• Этаж: необходимо выбрать этаж");
    }

    if (validationMessages.length > 0) {
      setError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    const terminalData = {
      name: formData.name,
      floor: formData.floor as number,
      // Устанавливаем дефолтные значения для масштаба (всегда 1.0)
      scaleX: 1.0,
      scaleY: 1.0,
      // Устанавливаем дефолтные значения для иконок (всегда дефолтная иконка)
      mapIconInactiveUrl: "/uploads/terminal/terminal.svg",
      mapIconActiveUrl: "/uploads/terminal/terminal.svg",
      positionX: formData.positionX,
      positionY: formData.positionY,
      positionZ: formData.positionZ,
      isActive: formData.isActive,
    };

    if (editingTerminal) {
      updateMutation.mutate({ id: editingTerminal.id, data: terminalData });
    } else {
      createMutation.mutate(terminalData);
    }
  };

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isModalOpen]);

  if (terminalsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>Загрузка стендов...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление стендами</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Стенды</h1>

        {error && (
          <div
            className={styles.errorMessage}
            style={{ whiteSpace: "pre-line" }}
          >
            {error}
          </div>
        )}
        {success && <div className={styles.successMessage}>{success}</div>}

        <button className={styles.addButton} onClick={handleAddNew}>
          + Добавить стенд
        </button>

        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableHeaderCell}>Название</th>
              <th className={styles.tableHeaderCell}>Этаж</th>
              <th className={styles.tableHeaderCell}>Позиция X</th>
              <th className={styles.tableHeaderCell}>Позиция Y</th>
              <th className={styles.tableHeaderCell}>Статус</th>
              <th className={styles.tableHeaderCell}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {terminals?.map((terminal) => (
              <tr key={terminal.id} className={styles.tableRow}>
                <td className={styles.tableCell}>{terminal.name}</td>
                <td className={styles.tableCell}>{terminal.floor}</td>
                <td className={styles.tableCell}>{terminal.positionX}</td>
                <td className={styles.tableCell}>{terminal.positionY}</td>
                <td className={styles.tableCell}>
                  <span
                    className={`${styles.badge} ${
                      terminal.isActive
                        ? styles.badgeActive
                        : styles.badgeInactive
                    }`}
                  >
                    {terminal.isActive ? "Активен" : "Неактивен"}
                  </span>
                </td>
                <td className={styles.tableCell}>
                  <button
                    className={styles.actionButton}
                    onClick={() => handleEdit(terminal)}
                  >
                    Редактировать
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.actionDanger}`}
                    onClick={() => handleDelete(terminal.id)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          className={`${styles.modal} ${isModalOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {editingTerminal ? "Редактировать стенд" : "Добавить стенд"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название *</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Этаж *</label>
                <select
                  className={styles.select}
                  value={formData.floor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      floor: e.target.value === "" ? "" : parseInt(e.target.value),
                    })
                  }
                  required
                >
                  <option value="">Выберите этаж</option>
                  {floors?.map((floor) => (
                    <option key={floor.id} value={floor.number}>
                      Этаж {floor.number} - {floor.name.ru}
                    </option>
                  ))}
                </select>
              </div>

              {/* Interactive floor map positioner */}
              {(() => {
                const selectedFloor = formData.floor !== "" 
                  ? floors?.find((f) => f.number === formData.floor)
                  : undefined;
                const floorMapUrl = selectedFloor
                  ? `${process.env.REACT_APP_MEDIA_PATH}${selectedFloor.mapUrl}`
                  : "";

                return floorMapUrl ? (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Установите позицию на карте:
                    </label>
                    <TerminalPositioner2D
                      floorMapUrl={floorMapUrl}
                      initialPositionX={formData.positionX}
                      initialPositionY={formData.positionY}
                      initialPositionZ={formData.positionZ}
                      onPositionChange={(pos) => {
                        setFormData({
                          ...formData,
                          positionX: pos.x,
                          positionY: pos.y,
                          positionZ: pos.z,
                        });
                      }}
                    />
                  </div>
                ) : null;
              })()}

              <h4
                style={{
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#333",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Или установите координаты вручную:
              </h4>

              <div className={styles.flexContainer}>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Позиция X</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.positionX}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionX: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Позиция Y</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.positionY}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionY: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
                <div className={styles.halfWidth}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Позиция Z</label>
                    <input
                      className={styles.input}
                      type="number"
                      step="0.01"
                      value={formData.positionZ}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          positionZ: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <div className={styles.checkboxContainer}>
                  <input
                    className={styles.checkboxInput}
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className={styles.checkboxLabel} htmlFor="isActive">
                    Активен
                  </label>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={() => setIsModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Сохранение..."
                    : editingTerminal
                    ? "Сохранить"
                    : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
